import React from "react";
import { Phone, Mail, MapPin, Clock, ArrowUpRight } from "lucide-react";
import PageHero from "../components/PageHero";
import { BRAND, IMG } from "../lib/content";

const details = [
  {
    icon: Phone,
    label: "Phone",
    value: BRAND.phone,
    valueEyebrow: BRAND.contactPerson,
    href: BRAND.phoneHref,
    testid: "contact-phone",
  },
  {
    icon: Mail,
    label: "Email",
    value: BRAND.email,
    href: BRAND.emailHref,
    testid: "contact-email",
  },
  {
    icon: MapPin,
    label: "Service Area",
    value: BRAND.serviceArea,
    href: null,
    testid: "contact-area",
  },
  {
    icon: Clock,
    label: "Hours",
    value: BRAND.hours,
    href: null,
    testid: "contact-hours",
  },
];

export default function Contact() {
  return (
    <main data-testid="contact-page">
      <PageHero
        eyebrow="Get in touch"
        title="Speak with a"
        italic="stone craftsman."
        subtitle="For free on-site inspections, honest advice and fixed-price quotes — reach us by phone or email. A senior craftsman will get back to you within one business day."
        image={IMG.harborView}
      />

      <section className="py-20 md:py-28 bg-white">
        <div className="max-w-7xl mx-auto px-6 md:px-10">
          <div className="flex items-center gap-3 text-[#B8965A]">
            <span className="block w-8 h-px bg-[#C9A86A]" />
            <span className="text-[11px] uppercase tracking-[0.3em]">Contact Details</span>
          </div>
          <h2 className="mt-6 font-display-bold text-4xl md:text-5xl lg:text-6xl tracking-tighter text-[#0A0A0A] max-w-3xl">
            The best way to reach us is{" "}
            <span className="font-serif-italic font-light text-[#B8965A]">directly.</span>
          </h2>

          <div className="mt-16 grid grid-cols-1 md:grid-cols-2 gap-px bg-black/10 border border-black/10">
            {details.map((d) => {
              const Wrapper = d.href ? "a" : "div";
              const props = d.href ? { href: d.href } : {};
              return (
                <Wrapper
                  key={d.label}
                  data-testid={d.testid}
                  {...props}
                  className="group bg-white p-6 md:p-12 flex items-start gap-4 md:gap-5 min-h-[160px] md:min-h-[180px] transition-colors hover:bg-[#F7F4F0]"
                >
                  <d.icon className="w-6 h-6 text-[#B8965A] mt-1 shrink-0" strokeWidth={1.4} />
                  <div className="flex-1 min-w-0">
                    <div className="text-[11px] uppercase tracking-[0.25em] text-[#525252]">
                      {d.label}
                    </div>
                    {d.valueEyebrow && (
                      <div
                        data-testid="contact-person-name"
                        className="mt-3 font-serif-italic text-[#B8965A] text-lg md:text-xl"
                      >
                        {d.valueEyebrow}
                      </div>
                    )}
                    <div className={`${d.valueEyebrow ? "mt-1" : "mt-3"} font-display-bold text-xl sm:text-2xl md:text-3xl tracking-tight text-[#0A0A0A] break-all md:break-words`}>
                      {d.value}
                    </div>
                  </div>
                  {d.href && (
                    <ArrowUpRight className="w-5 h-5 text-[#0A0A0A]/25 group-hover:text-[#B8965A] transition-colors shrink-0 mt-1" />
                  )}
                </Wrapper>
              );
            })}
          </div>

          <div className="mt-14 md:mt-16 grid grid-cols-1 lg:grid-cols-12 gap-10 items-start">
            <div className="lg:col-span-5">
              <h3 className="font-display-bold text-2xl md:text-3xl tracking-tight text-[#0A0A0A]">
                Sydney studio, <span className="font-serif-italic font-light text-[#B8965A]">on the road.</span>
              </h3>
              <p className="mt-5 text-[#525252] text-[15px] md:text-base leading-relaxed max-w-md">
                We come to you. Free, no-obligation on-site assessments across
                Sydney metro and the Central Coast. Call or email us with a few
                details about your project — we'll take it from there.
              </p>
              <div className="mt-8 flex flex-col sm:flex-row gap-3">
                <a
                  href={BRAND.phoneHref}
                  data-testid="contact-primary-call"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 bg-[#C9A86A] text-black uppercase tracking-[0.22em] text-[11px] hover:bg-[#B8965A] transition-colors"
                >
                  <Phone className="w-4 h-4" /> Call {BRAND.phone}
                </a>
                <a
                  href={BRAND.emailHref}
                  data-testid="contact-primary-email"
                  className="inline-flex items-center justify-center gap-2 px-8 py-4 border border-[#0A0A0A]/25 text-[#0A0A0A] uppercase tracking-[0.22em] text-[11px] hover:bg-[#0A0A0A] hover:text-white transition-colors"
                >
                  <Mail className="w-4 h-4" /> Email us
                </a>
              </div>
            </div>

            <div className="lg:col-span-7">
              <div className="aspect-[16/10] w-full overflow-hidden border border-black/10">
                <iframe
                  data-testid="contact-map"
                  title="Sydney service area map"
                  src="https://www.google.com/maps?q=Sydney%20NSW%20Australia&output=embed"
                  loading="lazy"
                  className="w-full h-full"
                  referrerPolicy="no-referrer-when-downgrade"
                />
              </div>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}
